// MoneyInput — text input that stores dollar amounts as integer cents.
//
// The old pattern (`<input type="number" step="0.01" value={(cents/100).toFixed(2)}
// onChange={... parseMoneyDollarsToCents}>`) re-formatted the displayed value on
// every keystroke, which made multi-digit typing impossible: typing "2579"
// would round-trip through `cents → "2.00" → cursor jump → "2.005" → "2.01"`
// and the user would be stuck. The spinner arrows also incremented by 1 cent
// per click, which is useless for dollar amounts.
//
// This component keeps a LOCAL raw-string state and only normalizes on blur.
// onChange still fires immediately so autosave + downstream computations see
// the new cents value, but the visible text never gets reformatted while the
// user is typing.

import { useEffect, useRef, useState } from 'react';
import { parseMoneyDollarsToCents } from '@/lib/money';

interface MoneyInputProps {
  valueCents: number;
  onChangeCents: (cents: number) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Optional aria-label or id forwarding (kept loose; consumer can add what they need). */
  ariaLabel?: string;
  /** Optional id forwarded to the underlying input — required for label htmlFor. */
  id?: string;
}

/**
 * Convert cents to a display string. Drops the `.00` suffix for whole-dollar
 * amounts so users see "1500" instead of "1500.00" — easier to read AND
 * easier to type into (cursor placement at end is natural).
 */
function centsToDisplayString(cents: number): string {
  if (cents === 0) return '';
  const dollars = cents / 100;
  if (Number.isInteger(dollars)) return String(dollars);
  return dollars.toFixed(2);
}

export function MoneyInput({
  valueCents,
  onChangeCents,
  className = 'input',
  placeholder,
  disabled,
  readOnly,
  ariaLabel,
  id,
}: MoneyInputProps) {
  const [raw, setRaw] = useState<string>(() => centsToDisplayString(valueCents));
  // Track which cents value the current raw represents. When valueCents
  // changes from an external source (e.g., draft load, reset, cascading
  // recompute from a different field), re-sync the display. When it
  // changes because of OUR onChange, raw is already current; don't clobber.
  const lastSyncedCents = useRef<number>(valueCents);

  useEffect(() => {
    if (valueCents !== lastSyncedCents.current) {
      const parsedFromRaw = parseMoneyDollarsToCents(raw);
      if (parsedFromRaw !== valueCents) {
        setRaw(centsToDisplayString(valueCents));
      }
      lastSyncedCents.current = valueCents;
    }
  }, [valueCents, raw]);

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      aria-label={ariaLabel}
      value={raw}
      onChange={(e) => {
        const newRaw = e.target.value;
        setRaw(newRaw);
        const cents = parseMoneyDollarsToCents(newRaw);
        lastSyncedCents.current = cents;
        onChangeCents(cents);
      }}
      onBlur={() => {
        // On blur, normalize the display to the canonical form derived from
        // the parsed cents. This catches things like "$1,234.56" → "1234.56",
        // trailing whitespace, or stray characters.
        const cents = parseMoneyDollarsToCents(raw);
        const normalized = centsToDisplayString(cents);
        if (normalized !== raw) setRaw(normalized);
      }}
    />
  );
}
